// Console delegation V1.0; 2026-10-07 09:05:47 CST; parent dcebd36069b2ddb3e45e586f2c90a0214b9a2a4f.
import { inDelegationContext } from '../auth/delegationSession';
import { httpClient } from '../api/client';
// 当前运营控制台源码维护说明，更新时间：2026-09-01 01:01 CST。后续修改本文件功能时，请同步更新注释、版本号和更新时间。
import { defineStore } from 'pinia';
import {
  clearConsoleSessionStorage,
  consoleApi,
  getConsoleSessionExpiresAt,
  saveConsoleSessionExpiry,
  tokenStorage
} from '../api/client';
import type { AuthTokenResponse, GoogleLoginPayload, LoginPayload, RegisterPayload } from '../api/types';

/*
 * Auth store owns only the local customer session lifecycle.
 * Keep customer isolation here strict: every accepted token must carry a valid customerId and expiry.
 * Do not accept admin tokens, forged tokens, or tokens without customer identity just to make a page load.
 */
export const useAuthStore = defineStore('auth', {
  state: () => ({
    accessToken: tokenStorage.getAccessToken(),
    customerId: tokenStorage.getCustomerId(),
    mustChangePassword: tokenStorage.getMustChangePassword(),
    sessionExpiresIn: null as number | null,
    sessionExpiresAt: null as string | null
  }),
  actions: {
    async login(payload: LoginPayload) {
      const token = await consoleApi.auth.login(payload);
      return this.applyToken(token);
    },
    async register(payload: RegisterPayload) {
      const token = await consoleApi.auth.register(payload);
      return this.applyToken(token);
    },
    async loginWithGoogle(payload: GoogleLoginPayload) {
      const token = await consoleApi.auth.googleLogin(payload);
      return this.applyToken(token);
    },
    applyToken(token: AuthTokenResponse) {
      const customerId = normalizeConsoleCustomerId(token.customerId);
      if (!customerId) {
        this.clearLocalSession();
        throw new Error('客户身份异常，请重新登录或联系支持。');
      }
      const expiresIn = normalizeConsoleExpiresIn(token.expiresIn);
      if (!expiresIn) {
        this.clearLocalSession();
        throw new Error('登录有效期异常，请重新登录。');
      }
      tokenStorage.save(token);
      const cappedExpiresIn = saveConsoleSessionExpiry(expiresIn);
      this.accessToken = token.accessToken;
      this.customerId = customerId;
      this.mustChangePassword = token.mustChangePassword;
      this.sessionExpiresIn = cappedExpiresIn;
      this.sessionExpiresAt = new Date(Date.now() + cappedExpiresIn * 1000).toISOString();
      return token;
    },
    async logout() {
      if (inDelegationContext()) {
        await httpClient.post('/api/v1/auth/delegation/end');
        this.clearLocalSession();
        return;
      }
      try {
        await consoleApi.auth.logout();
      } finally {
        this.clearLocalSession();
      }
    },
    clearLocalSession() {
      clearConsoleSessionStorage();
      this.accessToken = null;
      this.customerId = null;
      this.mustChangePassword = false;
      this.sessionExpiresIn = null;
      this.sessionExpiresAt = null;
    },
    syncFromStorage() {
      this.accessToken = tokenStorage.getAccessToken();
      this.customerId = tokenStorage.getCustomerId();
      this.mustChangePassword = tokenStorage.getMustChangePassword();
      this.sessionExpiresIn = null;
      const expiresAt = getConsoleSessionExpiresAt();
      this.sessionExpiresAt = expiresAt ? new Date(expiresAt).toISOString() : null;
    }
  }
});

function normalizeConsoleCustomerId(customerId: number | null): number | null {
  if (typeof customerId !== 'number') {
    return null;
  }
  return Number.isSafeInteger(customerId) && customerId > 0 ? customerId : null;
}

function normalizeConsoleExpiresIn(expiresIn: number): number | null {
  return Number.isFinite(expiresIn) && expiresIn > 0 ? expiresIn : null;
}
